"use client";

import React from "react";
import Link from "next/link";
import { Header } from "@/modules/shared/components/Header";
import { Footer } from "@/modules/shared/components/Footer";
import { PageTransition, FadeIn } from "@/modules/shared/components/PageTransition";
import { siteConfig } from "@/config/site.config";

export default function NosotrosClient() {
  const principios = [
    {
      num: "I",
      titulo: "Honestidad Intelectual",
      descripcion:
        "Sin artificios mercantiles ni promesas vacías. Apostamos por el estudio directo de las fuentes filosóficas, la lógica implacable y el rigor académico auténtico.",
    },
    {
      num: "II",
      titulo: "Construcción desde Cero",
      descripcion:
        "Iniciamos este proyecto con transparencia absoluta. Cada ensayo, lección y espacio se construye paso a paso, creciendo junto a nuestra comunidad.",
    },
    {
      num: "III",
      titulo: "Conocimiento Abierto y Gratuito",
      descripcion:
        "Creemos firmemente que pensar con rigor no debe ser un privilegio reservado a pocos. Todo lo que hacemos es y será 100% libre y gratuito.",
    },
    {
      num: "IV",
      titulo: "Comunidad en Formación",
      descripcion:
        "Buscamos a personas que valoren la dialéctica seria y la verdad. El Instituto se consolida con cada pensador que decide sumarse a este camino.",
    },
  ];

  const queHacemos = [
    {
      titulo: "Canal de YouTube",
      descripcion: "Serie que recorre la Historia de la Filosofía autor por autor, más análisis de psicología cognitiva.",
      enlace: "/canal",
    },
    {
      titulo: "Audiolibros Clásicos",
      descripcion: "Obras de filosofía e historia de la psicología de dominio público, leídas en cámara y por capítulos.",
      enlace: "/audiolibros",
    },
    {
      titulo: "Logos: El Podcast",
      descripcion: "Conversaciones sobre filosofía, poder y mente con notas estructuradas y bibliografía rigurosa.",
      enlace: "/podcast",
    },
    {
      titulo: "Ensayos & Artículos",
      descripcion: "Textos críticos breves con aparato crítico, citas verificables y rigor metodológico.",
      enlace: "/ensayos",
    },
    {
      titulo: "Círculo de Estudio",
      descripcion: "Grupo gratuito de lectura dialéctica y debate en Cusco y sincrónico para miembros a distancia.",
      enlace: "/circulo",
    },
  ];

  return (
    <PageTransition>
      <div className="min-h-screen bg-bg-main text-text-pure flex flex-col font-sans selection:bg-gold/30 selection:text-gold-light">
        <Header />

        <main className="flex-grow pt-24 md:pt-32 pb-20">
          
          {/* HERO */}
          <section className="relative w-full min-h-[50vh] flex items-center justify-center px-6 md:px-12 mb-16 overflow-hidden">
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(197,160,89,0.1),transparent_70%)] pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-b from-bg-main/60 via-transparent to-bg-main" />
            </div>

            <div className="relative z-10 text-center max-w-4xl mx-auto pt-6">
              <FadeIn>
                <div className="inline-flex items-center gap-2 border border-gold/35 bg-gold/5 px-4 py-1.5 rounded-full mb-6">
                  <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                  <span className="text-[10px] md:text-xs font-mono tracking-widest text-gold uppercase font-medium">
                    {siteConfig.lema} · {siteConfig.ubicacion}
                  </span>
                </div>

                <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl tracking-wide font-light mb-6">
                  Sobre el <span className="font-playfair italic text-gold">Instituto</span>
                </h1>

                <p className="text-text-muted/95 text-base sm:text-lg md:text-xl max-w-2xl mx-auto font-light leading-relaxed mb-6">
                  Un espacio independiente y 100% gratuito de filosofía, psicología y pensamiento estratégico, con sede en Cusco, Perú.
                </p>
              </FadeIn>
            </div>
          </section>

          <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-24">
            
            {/* MISIÓN */}
            <FadeIn delay={0.1}>
              <section className="p-8 sm:p-12 rounded-2xl border border-gold/20 bg-bg-secondary/30 relative">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-8 h-[1px] bg-gold/50" />
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Nuestra Razón de Ser
                  </span>
                </div>
                <h2 className="font-serif text-3xl text-text-pure font-light mb-6">
                  Misión: El Conocimiento Libre y Riguroso
                </h2>
                <div className="space-y-4 text-text-muted text-sm sm:text-base font-light leading-relaxed">
                  <p>
                    El Instituto Frederick Charles Copleston nace con una convicción clara y radical: el estudio profundo de la filosofía y las ciencias del comportamiento humano debe estar al alcance de toda persona que busque la verdad con honestidad, sin condicionamientos de pago ni artificios comerciales.
                  </p>
                  <p>
                    Desde la ciudad histórica de Cusco, articulamos un centro de producción y debate intelectual que recupera el espíritu de las academias clásicas: rigor conceptual, lectura directa de las fuentes y dialéctica respetuosa.
                  </p>
                </div>
              </section>
            </FadeIn>

            {/* QUÉ HACEMOS */}
            <FadeIn delay={0.15}>
              <section>
                <div className="text-center flex flex-col items-center mb-12">
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Estructura
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl text-text-pure mt-3 font-light">
                    Qué Hacemos
                  </h2>
                  <div className="w-12 h-[1px] bg-gold/50 mt-4" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {queHacemos.map((item, i) => (
                    <Link
                      key={i}
                      href={item.enlace}
                      className="p-6 rounded-xl border border-gold/15 bg-bg-secondary/20 hover:border-gold/40 hover:-translate-y-0.5 transition-all group flex flex-col justify-between"
                    >
                      <div>
                        <span className="text-[10px] font-mono text-gold/60 uppercase tracking-widest block mb-2">
                          Área {["01", "02", "03", "04", "05"][i]}
                        </span>
                        <h3 className="font-serif text-xl text-text-pure font-light mb-3 group-hover:text-gold-light transition-colors">
                          {item.titulo}
                        </h3>
                        <p className="text-text-muted text-xs leading-relaxed font-light mb-4">
                          {item.descripcion}
                        </p>
                      </div>
                      <span className="text-[10px] text-gold tracking-widest uppercase font-medium">
                        Conocer más →
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            </FadeIn>

            {/* CUATRO PRINCIPIOS FUNDACIONALES */}
            <FadeIn delay={0.2}>
              <section>
                <div className="text-center flex flex-col items-center mb-12">
                  <span className="text-[10px] md:text-xs font-semibold tracking-[0.4em] text-gold uppercase">
                    Fundamentos
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl text-text-pure mt-3 font-light">
                    Principios Fundacionales
                  </h2>
                  <div className="w-12 h-[1px] bg-gold/50 mt-4" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {principios.map((p) => (
                    <div
                      key={p.num}
                      className="p-8 rounded-xl border border-gold/15 bg-bg-secondary/20 relative"
                    >
                      <span className="font-serif text-3xl text-gold/60 block mb-3 font-light">
                        {p.num}
                      </span>
                      <h3 className="font-serif text-xl text-text-pure font-light mb-3">
                        {p.titulo}
                      </h3>
                      <p className="text-text-muted text-sm font-light leading-relaxed">
                        {p.descripcion}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            </FadeIn>

            {/* EL FUNDADOR */}
            <FadeIn delay={0.25}>
              <section className="p-8 sm:p-12 md:p-16 rounded-2xl border border-gold/25 bg-[#080b15] shadow-2xl relative">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                  <div className="md:col-span-4 flex flex-col items-center text-center">
                    <div className="relative w-44 h-44 rounded-2xl border border-gold/30 p-2 bg-bg-secondary/40 shadow-2xl flex items-center justify-center">
                      <div className="w-full h-full rounded-xl border border-gold/20 bg-[#06080e] flex flex-col items-center justify-center p-4">
                        <span className="font-serif text-4xl text-gold font-light tracking-wider mb-2">
                          EH
                        </span>
                        <span className="text-[9px] font-sans tracking-[0.3em] uppercase text-text-muted">
                          Cusco · Perú
                        </span>
                      </div>
                    </div>
                    <h3 className="font-serif text-xl text-text-pure mt-4 font-light">
                      {siteConfig.fundador.nombre}
                    </h3>
                    <p className="text-[10px] font-mono tracking-widest text-gold uppercase mt-1">
                      {siteConfig.fundador.titulo}
                    </p>

                    {/* Redes Sociales */}
                    <div className="flex items-center gap-3 mt-6">
                      <a
                        href={siteConfig.redes.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-full border border-gold/20 flex items-center justify-center text-text-muted hover:text-gold hover:border-gold transition-colors"
                        title="YouTube"
                      >
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                      </a>
                      <a
                        href={siteConfig.redes.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-full border border-gold/20 flex items-center justify-center text-text-muted hover:text-gold hover:border-gold transition-colors"
                        title="Instagram"
                      >
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                      </a>
                      <a
                        href={siteConfig.redes.tiktok}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-full border border-gold/20 flex items-center justify-center text-text-muted hover:text-gold hover:border-gold transition-colors"
                        title="TikTok"
                      >
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .8.11v-3.5a6.39 6.39 0 0 0-3.11.8 6.34 6.34 0 0 0-3.38 5.61 6.34 6.34 0 0 0 10.77 4.57 6.24 6.24 0 0 0 1.83-4.57V8.66a8.21 8.21 0 0 0 5.2 1.84V7.07a4.8 4.8 0 0 1-2-1.38 4.75 4.75 0 0 1-1.37-1z"/></svg>
                      </a>
                      <a
                        href={siteConfig.redes.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-full border border-gold/20 flex items-center justify-center text-text-muted hover:text-gold hover:border-gold transition-colors"
                        title="LinkedIn"
                      >
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.924 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      </a>
                    </div>
                  </div>

                  <div className="md:col-span-8 flex flex-col items-start text-left">
                    <span className="text-[10px] font-mono tracking-widest text-gold uppercase mb-3">
                      Perfil del Fundador
                    </span>
                    <h2 className="font-serif text-2xl sm:text-3xl text-text-pure font-light mb-6">
                      Edu Huallparimachi Huahuaccapa
                    </h2>
                    <div className="space-y-4 text-text-muted text-sm sm:text-base font-light leading-relaxed">
                      <p>{siteConfig.fundador.bioAmpliada}</p>
                    </div>
                  </div>
                </div>
              </section>
            </FadeIn>

            {/* IN MEMORIAM */}
            <FadeIn delay={0.3}>
              <section className="p-8 sm:p-12 rounded-2xl border border-gold/20 bg-bg-secondary/30 relative text-center flex flex-col items-center">
                <span className="text-[10px] font-mono tracking-widest text-gold/70 uppercase mb-3">
                  In Memoriam
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl text-text-pure font-light mb-2">
                  {siteConfig.inMemoriam.nombre}
                </h2>
                <p className="text-gold font-mono text-xs tracking-widest mb-6">
                  {siteConfig.inMemoriam.anios}
                </p>
                <div className="w-12 h-[1px] bg-gold/50 mb-6" />
                <p className="text-text-muted text-sm sm:text-base font-light leading-relaxed max-w-3xl mb-6">
                  {siteConfig.inMemoriam.semblanza}
                </p>
                <p className="text-xs font-mono text-gold/90 uppercase tracking-widest">
                  Obra cumbre: {siteConfig.inMemoriam.obraPrincipal}
                </p>
              </section>
            </FadeIn>

            {/* CONTACTO */}
            <FadeIn delay={0.35}>
              <section className="text-center p-8 sm:p-12 rounded-2xl border border-gold/15 bg-bg-secondary/20">
                <h3 className="font-serif text-2xl text-text-pure font-light mb-3">
                  Contacto Directo
                </h3>
                <p className="text-text-muted text-sm font-light mb-6">
                  Para consultas académicas, propuestas de colaboración o iniciativas de debate:
                </p>
                <a
                  href={`mailto:${siteConfig.correoContacto}`}
                  className="font-serif text-xl sm:text-2xl text-gold hover:text-gold-light transition-colors underline decoration-gold/40"
                >
                  {siteConfig.correoContacto}
                </a>
              </section>
            </FadeIn>

          </div>
        </main>

        <Footer />
      </div>
    </PageTransition>
  );
}
